import { useEffect, useState } from "react";
import { useAutoOpen } from "@/hooks/useAutoOpen";
import { api, formatApiError } from "@/lib/api";
import { rupiah, tanggalID, todayISO, toDateInput } from "@/lib/format";
import { PageHeader } from "@/components/app/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CurrencyInput } from "@/components/app/CurrencyInput";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, ShoppingCart } from "lucide-react";
import Pagination, { usePagination } from "@/components/app/Pagination";
import { ProdukStokCards } from "@/components/app/bakar/ProdukStokCards";
import AkunSelect from "@/components/app/AkunSelect";

const kgFmt = (v) => `${Number(v || 0).toLocaleString("id-ID", { maximumFractionDigits: 2 })} Kg`;
const empty = { tanggal: todayISO(), produk_id: "", supplier: "", kg: "", harga_per_kg: 0, ongkos_kirim: 0, keterangan: "" };

export default function PembelianArang() {
  const [items, setItems] = useState([]);
  const [produk, setProduk] = useState([]);
  const [ringkas, setRingkas] = useState([]);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(empty);
  const [loaded, setLoaded] = useState(false);

  const load = async () => {
    try {
      const [a, k, d] = await Promise.all([api.get("/pembelian-arang"), api.get("/kategori", { params: { tipe: "produk" } }), api.get("/dashboard/arang")]);
      setItems(a.data); setProduk(k.data.filter((p) => p.sumber === "beli"));
      setRingkas((d.data.per_produk || []).filter((p) => p.sumber === "beli"));
    } catch (e) { toast.error(formatApiError(e)); }
    setLoaded(true);
  };
  useEffect(() => { load(); }, []);

  const upd = (k) => (e) => setForm({ ...form, [k]: e && e.target ? e.target.value : e });
  const openNew = () => { setEditId(null); setForm({ ...empty, tanggal: todayISO(), produk_id: produk[0]?.id || "" }); setOpen(true); };
  useAutoOpen(openNew, loaded);
  const openEdit = (it) => { setEditId(it.id); setForm({ ...empty, ...it, tanggal: toDateInput(it.tanggal) || todayISO(), supplier: it.supplier ?? "", keterangan: it.keterangan ?? "" }); setOpen(true); };

  const subtotal = Number(form.kg || 0) * Number(form.harga_per_kg || 0);
  const total = subtotal + Number(form.ongkos_kirim || 0);
  const hpp = Number(form.kg) > 0 ? Math.round(total / Number(form.kg)) : 0;

  const save = async (e) => {
    e?.preventDefault();
    if (!form.tanggal) { toast.error("Tanggal wajib diisi"); return; }
    if (!form.produk_id) { toast.error("Pilih produk"); return; }
    if (!(Number(form.kg) > 0)) { toast.error("Jumlah (Kg) wajib diisi (lebih dari 0)"); return; }
    const payload = { tanggal: form.tanggal, produk_id: form.produk_id, supplier: form.supplier || "", kg: Number(form.kg),
      harga_per_kg: Number(form.harga_per_kg || 0), ongkos_kirim: Number(form.ongkos_kirim || 0), keterangan: form.keterangan || "", akun_id: form.akun_id || null };
    try {
      if (editId) await api.put(`/pembelian-arang/${editId}`, payload);
      else await api.post("/pembelian-arang", payload);
      toast.success(editId ? "Pembelian diperbarui" : "Pembelian tersimpan & stok arang bertambah");
      setOpen(false); await load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const del = async (id) => {
    if (!window.confirm("Hapus pembelian ini? Stok & pengeluaran terkait ikut berkurang.")) return;
    try { await api.delete(`/pembelian-arang/${id}`); toast.success("Terhapus"); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const { page, setPage, totalPages, paged, total: count, pageSize } = usePagination(items, 10);

  return (<>
    <PageHeader title="Pembelian Arang" subtitle="Arang jadi yang dibeli dari supplier (mis. Pendam, Batok Kelapa). Stok langsung bertambah & biaya masuk Pengeluaran."
      actions={<Button onClick={openNew} className="brand-btn rounded-sm" data-testid="add-beli-btn"><Plus size={14} className="mr-1"/>Pembelian Arang</Button>}/>
    <div className="p-4 md:p-8 space-y-4">
      <ProdukStokCards items={ringkas} testid="beli-stok-cards"/>
      <div className="card-flat overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-xs md:text-sm" data-testid="beli-table">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200 text-left text-[11px] uppercase tracking-wide text-slate-500">
                <th className="p-3">Tanggal</th><th className="p-3">Produk</th><th className="p-3">Supplier</th>
                <th className="p-3 text-right">Jumlah</th><th className="p-3 text-right">Harga/Kg</th><th className="p-3 text-right">Ongkos Kirim</th>
                <th className="p-3 text-right">Total</th><th className="p-3 text-right">HPP/Kg</th><th className="p-3 text-right">Aksi</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 && <tr><td colSpan="9" className="text-center py-10 text-slate-400">Belum ada pembelian arang.</td></tr>}
              {paged.map((it) => (
                <tr key={it.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`beli-row-${it.id}`}>
                  <td className="p-3 whitespace-nowrap">{tanggalID(it.tanggal)}</td>
                  <td className="p-3"><span className="text-[11px] px-2 py-0.5 rounded-sm bg-slate-100 text-slate-700 whitespace-nowrap">{it.produk_nama}</span></td>
                  <td className="p-3">{it.supplier || "-"}</td>
                  <td className="p-3 text-right font-mono font-semibold">{kgFmt(it.kg)}</td>
                  <td className="p-3 text-right font-mono">{rupiah(it.harga_per_kg)}</td>
                  <td className="p-3 text-right font-mono">{rupiah(it.ongkos_kirim)}</td>
                  <td className="p-3 text-right font-mono font-semibold">{rupiah(it.total_biaya)}</td>
                  <td className="p-3 text-right font-mono brand-text font-semibold">{rupiah(it.hpp_per_kg)}</td>
                  <td className="p-3">
                    <div className="flex items-center gap-1 justify-end">
                      <button onClick={() => openEdit(it)} className="p-1.5 rounded-sm hover:bg-slate-100" data-testid={`edit-beli-${it.id}`}><Pencil size={14}/></button>
                      <button onClick={() => del(it.id)} className="p-1.5 rounded-sm hover:bg-rose-50" data-testid={`del-beli-${it.id}`}><Trash2 size={14} className="text-rose-600"/></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Pagination page={page} setPage={setPage} totalPages={totalPages} total={count} pageSize={pageSize} testid="beli-pagination"/>
      </div>
    </div>

    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-lg" data-testid="beli-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><ShoppingCart size={18} className="brand-text"/>{editId ? "Ubah" : "Catat"} Pembelian Arang</DialogTitle></DialogHeader>
        <form onSubmit={save} className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2 space-y-1.5"><Label>Produk <span className="text-rose-500">*</span></Label>
            <Select value={form.produk_id || ""} onValueChange={upd("produk_id")}>
              <SelectTrigger data-testid="beli-produk"><SelectValue placeholder="Pilih produk"/></SelectTrigger>
              <SelectContent>{produk.map((p) => <SelectItem key={p.id} value={p.id}>{p.nama}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5"><Label>Tanggal <span className="text-rose-500">*</span></Label><Input type="date" value={form.tanggal} onChange={upd("tanggal")} data-testid="beli-tanggal"/></div>
          <div className="space-y-1.5"><Label>Supplier</Label><Input value={form.supplier} onChange={upd("supplier")} data-testid="beli-supplier"/></div>
          <div className="space-y-1.5"><Label>Jumlah (Kg) <span className="text-rose-500">*</span></Label><Input type="number" min="0" step="0.01" value={form.kg} onChange={upd("kg")} data-testid="beli-kg"/></div>
          <div className="space-y-1.5"><Label>Harga / Kg (Rp)</Label><CurrencyInput value={form.harga_per_kg} onValueChange={upd("harga_per_kg")} data-testid="beli-harga"/></div>
          <div className="md:col-span-2 space-y-1.5"><Label>Ongkos Kirim/Bongkar (Rp)</Label><CurrencyInput value={form.ongkos_kirim} onValueChange={upd("ongkos_kirim")} data-testid="beli-ongkos"/></div>
          <div className="md:col-span-2 grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Total Biaya</Label><div className="h-9 px-3 flex items-center border border-slate-200 bg-slate-50 font-mono font-semibold" data-testid="beli-total">{rupiah(total)}</div></div>
            <div className="space-y-1.5"><Label>HPP / Kg</Label><div className="h-9 px-3 flex items-center border border-slate-200 bg-blue-50 font-mono font-semibold brand-text" data-testid="beli-hpp">{rupiah(hpp)}</div></div>
          </div>
<AkunSelect className="md:col-span-2" label="Dibayar dari akun" value={form.akun_id} onChange={(v) => setForm((f) => ({ ...f, akun_id: v }))} testid="beli-akun"/>
          <div className="md:col-span-2 space-y-1.5"><Label>Keterangan</Label><Textarea value={form.keterangan} onChange={upd("keterangan")} data-testid="beli-keterangan"/></div>
          <button type="submit" className="hidden" aria-hidden="true"/>
        </form>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Batal</Button>
          <Button onClick={save} className="brand-btn rounded-sm" data-testid="beli-save">Simpan</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  </>);
}
